'use client';

import { c as _c } from "react/compiler-runtime";
import type { SortingState } from '@tanstack/react-table';
import { MERKL_DASHBOARD_URL } from '@morpho-org/dapp-core';
import { ExternalLink } from '@morpho-org/ui-v6/server';
import * as ScrollArea from '@radix-ui/react-scroll-area';
import { Button, Icon } from '@repo/ui';
import { ArrowDiagonalUp, Merkl32 } from '@repo/ui/icons';
import { useTable } from '@tanstack/react-table';
import { useState } from 'react';
import type { RewardContextProps } from '@/contexts/Reward/Reward.context';
import type { RewardsClaimSource } from '@/lib/analytics/client/events';
import { TableReward, TrBodyRowReward, TrHeaderGroupReward } from '@/components/common/Table';
import { useWeb3Context } from '@/contexts/Web3Context';
import { calculateRewardSum, isRewardSumFullyPriced } from '@/helpers/reward/calculateRewardSum';
import { useViewedAccountAddress } from '@/hooks/useViewedAccountAddress';
import { trackRewardsClaimClicked } from '@/lib/analytics/client/trackers/reward';
import { CardMessage } from '../CardNetApy/states';
import { columnLabelsRewards, rewardsColumnsDefs, rewardsTableFeatures } from './table/config';
export interface CardRewardProps {
  rewardData: RewardContextProps;
  /** Which hero card hosts this CTA, so the two surfaces compare in PostHog. */
  source: RewardsClaimSource;
}
const CardReward = t0 => {
  const $ = _c(46);
  const {
    rewardData,
    source
  } = t0;
  const {
    account
  } = useWeb3Context();
  const {
    isSelf
  } = useViewedAccountAddress();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [{
      id: "claimable",
      desc: true
    }];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [sortingCompact, setSortingCompact] = useState(t1);
  let t2;
  if ($[1] !== rewardData.display.rewardsMerged) {
    t2 = rewardsColumnsDefs.map(def => ({
      ...def,
      enableSorting: rewardData.display.rewardsMerged.length > 1
    }));
    $[1] = rewardData.display.rewardsMerged;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const _rewardsColumnsDefs = t2;
  let t3;
  if ($[3] !== sortingCompact) {
    t3 = {
      sorting: sortingCompact
    };
    $[3] = sortingCompact;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== _rewardsColumnsDefs || $[6] !== rewardData.display.rewardsMerged || $[7] !== t3) {
    t4 = {
      features: rewardsTableFeatures,
      data: rewardData.display.rewardsMerged,
      onSortingChange: setSortingCompact,
      manualSorting: false,
      state: t3,
      columns: _rewardsColumnsDefs,
      enableSortingRemoval: false,
      sortDescFirst: true,
      manualPagination: true
    };
    $[5] = _rewardsColumnsDefs;
    $[6] = rewardData.display.rewardsMerged;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const rewardsTable = useTable(t4);
  let t5;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = Array.from({
      length: 2
    }, _temp2);
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const skeletonRows = t5;
  let t6;
  if ($[10] !== rewardData.display.rewardsMerkl) {
    t6 = rewardData.display.rewardsMerkl.some(_temp3);
    $[10] = rewardData.display.rewardsMerkl;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const hasRewardsMerkl = t6;
  let t7;
  if ($[12] !== isSelf || $[13] !== rewardData.display.rewardsMerged || $[14] !== source) {
    t7 = () => {
      const rewards = rewardData.display.rewardsMerged;
      const canReportValue = isSelf && isRewardSumFullyPriced(rewards);
      trackRewardsClaimClicked({
        source,
        claimableUsdRaw: canReportValue ? calculateRewardSum(rewards) : undefined
      });
    };
    $[12] = isSelf;
    $[13] = rewardData.display.rewardsMerged;
    $[14] = source;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const handleClaimClick = t7;
  if (rewardData.errorCode === "UPSTREAM_UNAVAILABLE" || rewardData.errorCode === "UPSTREAM_SCHEMA") {
    let t8;
    if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = <CardMessage data-testid="reward-card-unavailable" message="Rewards temporarily unavailable."><LegacyRewardsNote /></CardMessage>;
      $[16] = t8;
    } else {
      t8 = $[16];
    }
    return t8;
  }
  if (rewardData.errorCode) {
    let t8;
    if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = <CardMessage message="Something went wrong." />;
      $[17] = t8;
    } else {
      t8 = $[17];
    }
    return t8;
  }
  if (rewardData.display.rewardsMerged.length === 0 && !rewardData.loading) {
    let t8;
    if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = <CardMessage message="You currently have no claimable or accruing rewards."><LegacyRewardsNote /></CardMessage>;
      $[18] = t8;
    } else {
      t8 = $[18];
    }
    return t8;
  }
  let t8;
  if ($[19] !== rewardsTable) {
    t8 = rewardsTable.getHeaderGroups().map(_temp4);
    $[19] = rewardsTable;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== t8) {
    t9 = <TableReward.Root><TableReward.Thead>{t8}</TableReward.Thead></TableReward.Root>;
    $[21] = t8;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== rewardData.loading || $[24] !== rewardsTable) {
    t10 = !rewardData.loading && rewardsTable.getRowModel().rows.map(_temp5);
    $[23] = rewardData.loading;
    $[24] = rewardsTable;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  let t11;
  if ($[26] !== rewardData.loading) {
    t11 = rewardData.loading && skeletonRows.map(_temp6);
    $[26] = rewardData.loading;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  let t12;
  if ($[28] !== t10 || $[29] !== t11) {
    t12 = <ScrollArea.Viewport className="max-h-[175px]"><TableReward.Root><TableReward.Tbody>{t10}{t11}</TableReward.Tbody></TableReward.Root></ScrollArea.Viewport>;
    $[28] = t10;
    $[29] = t11;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  let t13;
  let t14;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <ScrollArea.Scrollbar orientation="vertical" className="flex w-2 touch-none select-none"><ScrollArea.Thumb className="bg-border relative flex-1 rounded-lg" /></ScrollArea.Scrollbar>;
    t14 = <ScrollArea.Corner />;
    $[31] = t13;
    $[32] = t14;
  } else {
    t13 = $[31];
    t14 = $[32];
  }
  let t15;
  if ($[33] !== t12) {
    t15 = <ScrollArea.Root type="auto">{t12}{t13}{t14}</ScrollArea.Root>;
    $[33] = t12;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  let t16;
  if ($[35] !== t15 || $[36] !== t9) {
    t16 = <div>{t9}{t15}</div>;
    $[35] = t15;
    $[36] = t9;
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  let t17;
  if ($[38] !== account || $[39] !== handleClaimClick || $[40] !== hasRewardsMerkl || $[41] !== rewardData.loading) {
    t17 = !rewardData.loading && <div className="border-border sticky z-2 w-full border-t-[0.5px]"><div className="p-md gap-sm flex flex-col">{hasRewardsMerkl && account?.address && <ExternalLink data-testid="claim-merkl-button" href={`${MERKL_DASHBOARD_URL}/${account.address}`} onClick={handleClaimClick}><Button style={{
            width: "100%"
          }} size="lg"><div className="gap-2xs flex items-center"><span className="text-primary-foreground text-sm" style={{
                lineHeight: "normal"
              }}>Claim on</span><Merkl32 aria-label="Merkl" className="text-primary-foreground" style={{
                height: 12,
                width: "auto"
              }} /><Icon icon={<ArrowDiagonalUp />} /></div></Button></ExternalLink>}<LegacyRewardsNote /></div></div>;
    $[38] = account;
    $[39] = handleClaimClick;
    $[40] = hasRewardsMerkl;
    $[41] = rewardData.loading;
    $[42] = t17;
  } else {
    t17 = $[42];
  }
  let t18;
  if ($[43] !== t16 || $[44] !== t17) {
    t18 = <div data-testid="reward-card" className="flex min-h-[345px] flex-col justify-between max-lg:h-auto">{t16}{t17}</div>;
    $[43] = t16;
    $[44] = t17;
    $[45] = t18;
  } else {
    t18 = $[45];
  }
  return t18;
};
const LegacyRewardsNote = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <span className="text-2xs text-muted-foreground">Legacy rewards aren't displayed in the app. Check and claim{" "}<ExternalLink href="https://rewards-legacy.morpho.org" style={{
        textDecoration: "underline"
      }}>here</ExternalLink>.</span>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};
export default CardReward;
function _temp(def_0) {
  return def_0.skeletonCell;
}
function _temp2() {
  return rewardsColumnsDefs.map(_temp);
}
function _temp3(reward) {
  return reward.claimable?.amount;
}
function _temp4(headerGroup) {
  return <TrHeaderGroupReward key={headerGroup.id} headerGroup={headerGroup} columnLabels={columnLabelsRewards} />;
}
function _temp5(row) {
  return <TrBodyRowReward key={row.id} row={row} />;
}
function _temp6(row_0, index) {
  return <TrBodyRowReward key={index + "skeleton-table-reward"} skeletonRow={row_0} />;
}